import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';

import {
  useStaffCan,
  useI18n,
  useSelector,
  useIntegrationCheck,
  useBridgeApi,
  useLogger,
  useDispatch,
  useLicenses,
  useButton,
} from '~Hooks';
import * as actions from '../../actions';
import State from '~Types/state';
import { User } from '~Types';
import Microdeb from '~/Components/Microdeb';

import logo from '~Assets/Logos/baemingo_logo_white.svg';
import History from '~Assets/icons/history.svg';
import ReceiptFilled from '~Assets/icons/receipt.svg';
import ReceiptOutlined from '~Assets/icons/MainMenu/receipt-outlined.svg';
import CheckbookFilled from '~Assets/icons/MainMenu/checkbook-filled.svg';
import CheckbookOutlined from '~Assets/icons/MainMenu/checkbook-outlined.svg';
import HomeOutlined from '~Assets/icons/MainMenu/home-outlined.svg';
import HomeFilled from '~Assets/icons/MainMenu/home-filled.svg';
import CalendarCheckFilled from '~Assets/icons/MainMenu/calendar-check-filled.svg';
import CalendarCheckOutlined from '~Assets/icons/MainMenu/calender-check-outlined.svg';
import Inventory from '~Assets/icons/boxes_stacked.svg';
import MoneyFilled from '~Assets/icons/MainMenu/money-filled.svg';
import MoneyOutlined from '~Assets/icons/MainMenu/money-outlined.svg';
import SettingsFilled from '~Assets/icons/settings.svg';
import SettingsOutlined from '~Assets/icons/MainMenu/settings-outlined.svg';
import TransactionsOutlined from '~Assets/icons/payment.svg';
import TransactionsFilled from '~Assets/icons/MainMenu/credit-card-filled.svg';
import GroupFilled from '~Assets/icons/MainMenu/group-filled.svg';
import GroupOutlined from '~Assets/icons/MainMenu/group-outlined.svg';
import GiftCardIcon from '~Assets/icons/redeem.svg';
import TrophyFilled from '~Assets/icons/MainMenu/trophy-filled.svg';
import TrophyOutlined from '~Assets/icons/trophy.svg';
import VersionIcon from '~Assets/icons/MainMenu/version.svg';
import DeviceIcon from '~Assets/icons/pos.svg';
import PersonIcon from '~Assets/icons/person.svg';
import StoreFrontIcon from '~Assets/icons/storefront.svg';
import LoyaltyOutlinedIcon from '~Assets/icons/loyalty.svg';
import LoyaltyFilledIcon from '~Assets/icons/MainMenu/loyalty-filled.svg';
import { useStorage } from '~Components/Storage';
import { gql, useQuery } from '@apollo/client';
import { useDeviceHandler } from '~Components/DeviceProvider';
import Retain24Payment from '~Components/Retain24';
import QuestionModal from '~ComponentsImproved/QuestionModal';
import { Box, TransparentButton } from '~Styles';
import { GetStores } from './__queries__';
import { useCompetition } from '~Providers/CompetitionsProvider';
import MenuItem, { MenuItemType } from './MenuItem';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import useModal from '~ComponentsImproved/Modal/useModal';
import Icon from '~ComponentsImproved/Icon';

const BackPanel = styled.div<{ menuIsVisible: boolean }>`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  background: var(--colorDimmerBackground);
  left: 0;
  z-index: 20;
  will-change: opacity;
  transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1);
  opacity: ${(props) => (props.menuIsVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.menuIsVisible ? 'all' : 'none')};
`;

const Wrapper = styled(Box)<{ menuIsVisible: boolean }>`
  width: 272px;
  height: 100%;
  position: fixed;
  top: 0;
  padding-top: 24px;
  left: ${(props) => (props.menuIsVisible ? '0' : '-272px')};
  z-index: 20;
  will-change: transform;
  background: var(--colorBlueGray1200);
  box-sizing: border-box;
  box-shadow: ${(props) =>
    props.menuIsVisible ? '16px 0px 32px 0px rgba(0, 0, 0, 0.32)' : 'none'};
  transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1);
`;

const MenuItemsWrapper = styled(Box)`
  flex: 1;
`;

const Logo = styled.div`
  margin-top: 8px;
  height: 16px;
  mask-image: url(${logo});
  width: 188px;
  mask-size: contain;
  background-color: var(--colorWhite);
  mask-repeat: no-repeat;
`;

const BottomPlacement = styled(Box)`
  margin-top: auto;
`;

const BottomBox = styled(Box)`
  border-top: 1px solid var(--colorBlueGray800);
`;

const STORE_QUERY = gql`
  query GetStores {
    operativeViewer {
      id
      adyenStore {
        id
        name
      }
    }
  }
`;

interface Props {
  id: string;
  menuIsVisible: boolean;
  toggleMainMenu: (isVisible: boolean) => void;
  setModulesWebPage: (url: string | null) => void;
  user: User;
  version: string;
  visibleCurrentProduct: boolean;
  companyId: string;
}

const MainMenu: FC<Props> = ({
  id,
  menuIsVisible,
  toggleMainMenu,
  setModulesWebPage,
  user,
  version,
}) => {
  const { i18n } = useI18n();

  const storeQuery = useQuery<GetStores>(STORE_QUERY);
  const hasStore = Boolean(storeQuery?.data?.operativeViewer?.adyenStore);

  const logger = useLogger('mount');
  const dispatch = useDispatch();
  const api = useBridgeApi();
  const [storage] = useStorage();
  const licenses = useLicenses();
  const deviceHandler = useDeviceHandler();
  const competitions = useCompetition();

  const demoMode = useSelector((state) => state.demoMode);
  const flags = useSelector((state) => state.companyFlags);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const posPayments = useSelector((state) => state.POSPayments);
  const enableAutoCashEOD = useSelector((state) => state.enableAutoCashEOD);
  const venueName = useSelector((state) => state.venueName);
  const country = useSelector((state) => state.country);

  const [showMicrodebDeposit, setShowMicrodebDeposit] = useState<boolean>(false);
  const [showRetain24Deposit, setShowRetain24Deposit] = useState<boolean>(false);

  const reloadModal = useModal();

  const deviceNameToShow = flags.useNewLicenses
    ? deviceHandler.activeDevice?.name
    : storage.taxComplianceDeviceName;

  const hasPunchClockIntegration = useIntegrationCheck('PERSONALKOLLEN');

  const staffCan = useStaffCan();
  const hasBaemingoGiftCard = paymentMethods.some((m) => m.id === 'BAEMINGO_GIFTCARD');

  const reloadApp = () => {
    if (posPayments.length > 0) {
      logger.info('Reload not allowed: Ongoing payments');
      dispatch(
        actions.addToast(i18n('Menu.PoweredBy.Reload.NotAllowed'), true, 'ongoing-payment', true),
      );
      return;
    }

    logger.info('Location reload: PoweredBy');
    setTimeout(() => {
      api.reload();
    }, 100);
  };

  const reloadButton = useButton({
    onClick: reloadModal.show,
  });

  const MENU_ITEMS: MenuItemType[] = [
    {
      id: 'history',
      label: i18n('Menu.History'),
      iconOutlined: History,
      iconFilled: History,
      link: `/venue/${id}/history-v2`,
      allowedToRender: staffCan.HISTORY && !demoMode,
    },
    {
      id: 'payments-refunds',
      label: i18n('Menu.PaymentsRefunds'),
      iconOutlined: TransactionsOutlined,
      iconFilled: TransactionsFilled,
      link: `/venue/${id}/transactions`,
      allowedToRender: staffCan.HISTORY && !demoMode && hasStore,
    },
    {
      id: 'giftcards',
      label: i18n('Menu.GiftCards'),
      iconOutlined: GiftCardIcon,
      iconFilled: GiftCardIcon,
      link: `/venue/${id}/gift-cards`,
      allowedToRender: licenses.hasGiftcard && staffCan.GIFT_CARDS && hasBaemingoGiftCard,
    },
    {
      id: 'reports',
      label: i18n('Menu.Reports'),
      iconOutlined: ReceiptOutlined,
      iconFilled: ReceiptFilled,
      link: `/venue/${id}/reports`,
      allowedToRender: !demoMode && (staffCan.REPORTS || storage.staffCanViewOwnReport),
    },
    {
      id: 'z-reports',
      label: i18n('Menu.ZReports'),
      iconOutlined: ReceiptOutlined,
      iconFilled: ReceiptFilled,
      link: `/venue/${id}/z-reports-new`,
      allowedToRender: staffCan.REPORTS,
    },
    {
      id: 'end-of-day',
      label: i18n('Menu.EndOfDay'),
      iconOutlined: CalendarCheckOutlined,
      iconFilled: CalendarCheckFilled,
      link: `/venue/${id}/end-of-day`,
      allowedToRender: staffCan.CLOSE_DAY && flags.manualEndOfDay && !enableAutoCashEOD,
    },
    {
      id: 'cash-management',
      label: i18n('Menu.CashManagement'),
      iconOutlined: MoneyOutlined,
      iconFilled: MoneyFilled,
      link: `/venue/${id}/cash-sources`,
      allowedToRender: !demoMode && paymentMethods.some((method) => method.id === 'CASH'),
    },
    {
      id: 'inventory',
      label: i18n('Menu.InventoryManagment'),
      iconOutlined: Inventory,
      iconFilled: Inventory,
      link: `/venue/${id}/inventory`,
      allowedToRender: !demoMode && licenses.hasInventory,
    },
    {
      id: 'members',
      label: i18n('Menu.Members'),
      iconOutlined: LoyaltyOutlinedIcon,
      iconFilled: LoyaltyFilledIcon,
      link: `/venue/${id}/members`,
      allowedToRender: flags.hasPoints || country === 'ES',
    },
    {
      id: 'microdeb-deposit',
      label: i18n('Menu.GiftcardDeposit') + ' (M)',
      iconOutlined: GiftCardIcon,
      iconFilled: GiftCardIcon,
      active: showMicrodebDeposit,
      onClick: () => setShowMicrodebDeposit(true),
      allowedToRender: paymentMethods.some((method) => method.id === 'MICRODEB'),
    },
    {
      id: 'retain24-deposit',
      label: i18n('Menu.GiftcardDeposit') + ' (R24)',
      iconOutlined: GiftCardIcon,
      iconFilled: GiftCardIcon,
      active: showRetain24Deposit,
      onClick: () => setShowRetain24Deposit(true),
      allowedToRender: paymentMethods.some((method) => method.id === 'RETAIN24'),
    },
    {
      id: 'expenses',
      label: i18n('Menu.Expenses'),
      iconOutlined: CheckbookOutlined,
      iconFilled: CheckbookFilled,
      link: `/venue/${id}/expenses`,
      allowedToRender:
        paymentMethods.some((method) => method.id === 'ADYEN') && staffCan.MANUAL_DISCOUNT,
    },
    {
      id: 'competitions',
      label: i18n('Competitions.Title'),
      iconOutlined: TrophyOutlined,
      iconFilled: TrophyFilled,
      link: `/venue/${id}/competitions`,
      allowedToRender: competitions.onGoing.length > 0 || competitions.upcoming.length > 0,
    },
  ];

  return (
    <>
      <BackPanel
        onClick={() => {
          toggleMainMenu(!menuIsVisible);
        }}
        menuIsVisible={menuIsVisible}
      />

      <Wrapper
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        menuIsVisible={menuIsVisible}>
        <MenuItemsWrapper display="flex" flexDirection="column">
          <MenuItem
            key="home"
            item={{
              id: 'home',
              label: i18n('Menu.Home'),
              iconOutlined: HomeOutlined,
              iconFilled: HomeFilled,
              allowedToRender: true,
              link: `/venue/${id}/gateway`,
            }}
            closeMainMenu={() => toggleMainMenu(false)}
          />

          {MENU_ITEMS.map((item) => {
            return (
              <MenuItem key={item.id} item={item} closeMainMenu={() => toggleMainMenu(false)} />
            );
          })}

          <BottomPlacement display="flex" flexDirection="column" fluid>
            <MenuItem
              key="punch-clock"
              item={{
                id: 'punch-clock',
                label: i18n('Menu.PunchIn'),
                iconOutlined: GroupOutlined,
                iconFilled: GroupFilled,
                onClick: () => setModulesWebPage('http://personalkollen.se/stämpelklocka'),
                allowedToRender: hasPunchClockIntegration,
              }}
              closeMainMenu={() => toggleMainMenu(false)}
            />
            <MenuItem
              key="settings"
              item={{
                id: 'settings',
                label: i18n('Menu.Settings'),
                iconOutlined: SettingsOutlined,
                iconFilled: SettingsFilled,
                allowedToRender: staffCan.SETTINGS,
                link: `/venue/${id}/settings-v2`,
              }}
              closeMainMenu={() => toggleMainMenu(false)}
            />
          </BottomPlacement>
        </MenuItemsWrapper>

        <BottomBox display="flex" flexDirection="column" gap="medium" padding="small">
          <TransparentButton
            display="flex"
            flexDirection="column"
            padding="none"
            {...reloadButton}
            alignItems="flex-start">
            <Paragraph level={5} color="var(--colorBlueGray200)">
              {i18n('Menu.PoweredBy')}
            </Paragraph>
            <Logo />
          </TransparentButton>

          <Box display="flex" flexDirection="column" gap="xs">
            {version && (
              <Box display="flex" gap="xs" alignItems="center">
                <Icon
                  src={VersionIcon}
                  height="16px"
                  width="16px"
                  color="var(--colorBlueGray200)"
                />
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {version}
                </Paragraph>
              </Box>
            )}
            {user?.name && (
              <Box display="flex" gap="xs" alignItems="center">
                <Icon
                  src={PersonIcon}
                  height="16px"
                  width="16px"
                  color="var(--colorBlueGray200)"
                />
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {user.name}
                </Paragraph>
              </Box>
            )}
            <Box display="flex" gap="xs" alignItems="center">
              <Icon src={DeviceIcon} height="16px" width="16px" color="var(--colorBlueGray200)" />
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {deviceNameToShow || i18n('Settings.Unknown')}
              </Paragraph>
            </Box>
            {venueName && (
              <Box display="flex" gap="xs" alignItems="center">
                <Icon
                  src={StoreFrontIcon}
                  height="16px"
                  width="16px"
                  color="var(--colorBlueGray200)"
                />
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {venueName}
                </Paragraph>
              </Box>
            )}
          </Box>
        </BottomBox>
      </Wrapper>

      {showMicrodebDeposit && (
        <Microdeb
          amount={10}
          isFailing={false}
          onComplete={() => {
            setShowMicrodebDeposit(false);
          }}
          type="DEPOSIT"
          onFail={() => setShowMicrodebDeposit(false)}
        />
      )}

      {showRetain24Deposit && (
        <Retain24Payment
          id={`r24_${uuid()}`}
          amount={10}
          onFail={() => setShowRetain24Deposit(false)}
          onComplete={() => setShowRetain24Deposit(false)}
          type="DEPOSIT"
        />
      )}

      <QuestionModal
        question={i18n('Menu.PoweredBy.ReloadQuestion')}
        isShowing={reloadModal.isShowing}
        onLeftClick={reloadModal.hide}
        onRightClick={reloadApp}
        backdropClose={reloadModal.hide}
      />
    </>
  );
};

const mapStateToProps = (state: State) => ({
  user: state.user,
  menuIsVisible: state.mainMenu,
  locale: state.locale,
  version: state.appVersion,
  companyId: state.company,
});

const mapDispatchToProps = {
  setModulesWebPage: actions.setModulesWebpage,
  toggleMainMenu: actions.toggleMainMenu,
  changeLocale: actions.changeLocale,
};

export default _.flowRight(withRouter, connect(mapStateToProps, mapDispatchToProps))(MainMenu);
